import { salvarJson } from '../core';
import { app, jogoAtual } from './app';
import { verificarNovidades } from './notificacoes';
import { chamarApi, sessaoDaConta } from './online';

/**
 * Selo do jogo solo: com uma conta logada, o navegador manda o save ao servidor (comprimido) depois de cada rodada; o servidor confere
 * se ele continua o último selo da conta, credita as vitórias e os títulos (conquistas e XP) e devolve um selo assinado. Ao exportar,
 * o arquivo leva o selo; ao importar, o servidor diz se o arquivo é autêntico ou foi alterado.
 */

export interface ResultadoSelo {
  selo: string;
  vitorias: number;
  titulos: number;
  xp: number;
  nivel: number;
  aviso?: string;
}

/** Último resultado do selo do jogo aberto, para mostrar em Opções. */
export const estadoSelo: { texto: string; ultimaRodada: string | null; ok: boolean } = { texto: '', ultimaRodada: null, ok: true };
/** Selo de um arquivo autêntico recém-importado (usado no primeiro selo desse jogo na conta). */
let seloImportado: string | null = null;
let selando: Promise<ResultadoSelo | null> | null = null;
let ultimoEnvio = 0;

export function definirSeloImportado(selo: string | null): void {
  seloImportado = selo;
  ultimaTentativa = '';
}
let ultimaTentativa = '';

export async function comprimirTexto(texto: string): Promise<string> {
  const fluxo = new CompressionStream('gzip');
  const w = fluxo.writable.getWriter();
  void w.write(new TextEncoder().encode(texto)).then(() => w.close());
  const bytes = new Uint8Array(await new Response(fluxo.readable).arrayBuffer());
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

export async function sha256Hex(texto: string): Promise<string> {
  const h = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(texto));
  return [...new Uint8Array(h)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Manda o jogo aberto para o servidor e devolve o selo (null se não há conta, está online ou o servidor recusou). */
export async function selarAgora(texto = salvarJson(jogoAtual())): Promise<ResultadoSelo | null> {
  const conta = sessaoDaConta();
  if (!conta || app.online || !app.jogo) return null;
  if (selando) return selando;
  selando = (async () => {
    try {
      const e = app.jogo as NonNullable<typeof app.jogo>;
      const r = await chamarApi<ResultadoSelo>('/conta/solo', { token: conta.token, gz: await comprimirTexto(texto), ...(seloImportado ? { seloAnterior: seloImportado } : {}) });
      seloImportado = null;
      ultimoEnvio = Date.now();
      ultimaTentativa = `${e.semente}:${e.temporada}:${e.rodada}`;
      estadoSelo.ok = true;
      estadoSelo.ultimaRodada = `temporada ${e.temporada}, rodada ${e.rodada}`;
      estadoSelo.texto = r.aviso ?? (r.vitorias || r.titulos ? `+${r.xp} XP (nível ${r.nivel})` : '');
      // o servidor acabou de creditar XP e conquistas: confere o perfil para avisar nível novo e conquistas liberadas
      void verificarNovidades();
      return r;
    } catch (erro) {
      estadoSelo.ok = false;
      estadoSelo.texto = erro instanceof Error ? erro.message : 'selo recusado';
      return null;
    } finally {
      selando = null;
    }
  })();
  return selando;
}

/** Chamado depois de cada gravação do jogo solo: sela quando a rodada mudou (no máximo uma vez a cada 6 segundos). */
export function selarSePreciso(): void {
  const conta = sessaoDaConta();
  if (!conta || app.online || !app.jogo) return;
  // com a partida em andamento o resultado ainda pode mudar (substituições refazem a rodada): sela depois, quando ela fechar
  if (app.tela === 'partida') return;
  const e = app.jogo;
  const marca = `${e.semente}:${e.temporada}:${e.rodada}`;
  if (marca === ultimaTentativa || Date.now() - ultimoEnvio < 6000) return;
  void selarAgora().then((r) => {
    if (!r) ultimaTentativa = marca; // recusado: não insiste na mesma rodada
  });
}

/** Confere um arquivo importado: devolve o resumo se o selo for nosso e o conteúdo não foi alterado. */
export async function verificarArquivo(selo: string, texto: string): Promise<{ autentico: boolean; conta?: string; temporada?: number; rodada?: number }> {
  try {
    return await chamarApi('/conta/solo-verificar', { selo, hash: await sha256Hex(texto) });
  } catch {
    return { autentico: false };
  }
}
